import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Code2, Globe, Sparkles, Zap, AlertTriangle, BookOpen } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { QuickStartTab } from './QuickStartTab';
import { ModernFrameworksTab } from './ModernFrameworksTab';
import { CMSPlatformsTab } from './CMSPlatformsTab';
import { TroubleshootingTab } from './TroubleshootingTab';
import { BestPracticesTab } from './BestPracticesTab';

export function WebIntegrationHub() {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  
  return (
    <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white overflow-hidden hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
      <CardHeader className="bg-gradient-to-r from-purple-50/80 to-emerald-50/80 border-b border-slate-200/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <Code2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold text-slate-900">{t('websiteIntegrationHub')}</CardTitle>
              <p className="text-slate-600 text-sm">
                Complete integration guide - from quick setup to advanced customization
              </p>
            </div>
          </div>
          {/* Quick Action Button */}
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="outline"
              className="border-purple-200 text-purple-700 hover:bg-purple-50 shadow-sm"
              onClick={() => window.open('/chatbot-test.html', '_blank')}
            >
              <Globe className="w-4 h-4 mr-2" />
              {t('testPage')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <Tabs defaultValue="quick-start" className="space-y-6">
          <TabsList className="bg-slate-50/80 p-1 rounded-lg shadow-sm border border-slate-200/60 h-12">
            <TabsTrigger 
              value="quick-start" 
              className="flex items-center gap-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              title="Universal integration codes for any website"
            >
              <Sparkles className="w-4 h-4" />
              Quick Start
            </TabsTrigger>
            <TabsTrigger 
              value="frameworks" 
              className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              title="React, Vue, Next.js integration examples"
            >
              <Zap className="w-4 h-4" />
              Frameworks
            </TabsTrigger>
            <TabsTrigger 
              value="platforms" 
              className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
              title="WordPress, Google Sites, Wix step-by-step guides"
            >
              <Globe className="w-4 h-4" />
              CMS Platforms
            </TabsTrigger>
            <TabsTrigger 
              value="troubleshooting" 
              className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
            >
              <AlertTriangle className="w-4 h-4" />
              {t('troubleshooting')}
            </TabsTrigger>
            <TabsTrigger 
              value="best-practices" 
              className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
            >
              <BookOpen className="w-4 h-4" />
              {t('bestPractices')}
            </TabsTrigger>
          </TabsList>

          {/* Quick Start Tab - Main Integration Hub */}
          <TabsContent value="quick-start">
            <QuickStartTab />
          </TabsContent>

          {/* Modern Frameworks Tab */}
          <TabsContent value="frameworks">
            <ModernFrameworksTab />
          </TabsContent>

          {/* CMS Platforms Tab */}
          <TabsContent value="platforms">
            <CMSPlatformsTab />
          </TabsContent>

          {/* Troubleshooting Tab */}
          <TabsContent value="troubleshooting">
            <TroubleshootingTab />
          </TabsContent>

          {/* Best Practices Tab */}
          <TabsContent value="best-practices">
            <BestPracticesTab />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
} 